import React, { FC, ReactNode, useMemo } from "react";
import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { COLORS, FONTS, RADII, FONT_FAMILIES, FONT_WEIGHTS, SIZES } from "../../common/consts";
import { ReactComponent as SelectDown } from "../../assets/icons/select-down.svg";

export interface SearchableSelectOption {
  value: string;
  label?: ReactNode;
  disabled?: boolean;
  /** Muted second line (e.g. an internal name); also matched by the search. */
  secondary?: string;
  /** Always listed, whatever is typed (e.g. a "+ Create new" action). */
  pinned?: boolean;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  renderValue?: (value: string) => ReactNode;
  fullWidth?: boolean;
  loading?: boolean;
  /** Also accept text that is not one of the options, reported as it is typed. */
  freeSolo?: boolean;
  /** An option was chosen, or (freeSolo) Enter pressed on typed text; never called per keystroke. */
  onPick?: (value: string) => void;
}

const StyledAutocomplete = styled(Autocomplete<string, false, false, boolean>)`
  && {
    .MuiOutlinedInput-root {
      border-radius: ${RADII.md};
      font: ${FONTS.sm};
      color: var(--color-foregroundTertiary, ${COLORS.foregroundTertiary});
      background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});

      /* Border lives on the notched outline (not the root) so the label gets a
         real gap in the border, and the field matches Input's geometry. */
      .MuiOutlinedInput-notchedOutline {
        border: 1px solid var(--color-borderTertiary, ${COLORS.borderTertiary});
        border-radius: ${RADII.md};
      }

      &:hover .MuiOutlinedInput-notchedOutline {
        border-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
        border-width: 1px;
      }

      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
        border-width: 1px;
      }

      /* Autocomplete adds its own padding on top of the input's; drop it so the
         field is exactly as tall as a text field or a plain Select. */
      &.MuiAutocomplete-inputRoot {
        padding: 0 !important;
      }

      .MuiInputBase-input {
        min-height: unset !important;
        font-family: ${FONT_FAMILIES.regular};
        font-size: ${SIZES.inputFieldFontSize} !important;
        font-weight: ${SIZES.inputFieldFontWeight};
        /* height:auto so the 23px line box drives the height, as in Input. */
        height: auto;
        line-height: 23px;
        padding: 8px 32px 8px 20px !important;
      }
    }
    
    .MuiAutocomplete-endAdornment {
      /* The popup indicator is an IconButton whose default colour ignores the
         theme, and the arrow is a plain currentColor svg, not an MuiSvgIcon. */
      .MuiAutocomplete-popupIndicator,
      .MuiAutocomplete-clearIndicator,
      .MuiSvgIcon-root,
      svg {
        color: var(--color-dropdownIconColor, ${COLORS.dropdownIconColor});
      }
    }
  }
`;

const StyledTextField = styled(TextField)`
  && {
    .MuiInputLabel-root {
      /* No background chip — the outline's legend notches around the label. */
      color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary}) !important;
      font-family: ${FONT_FAMILIES.regular} !important;
      font-weight: ${FONT_WEIGHTS.light} !important;
      font-size: ${SIZES.inputLabelFontSize} !important;
      
      &.Mui-focused {
        color: var(--color-inputLabel, ${COLORS.inputLabel}) !important;
      }
      
      &.MuiInputLabel-shrink {
        color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary}) !important;
      }
    }
  }
`;

const SearchableSelect: FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  disabled,
  placeholder,
  label,
  renderValue,
  fullWidth = true,
  loading = false,
  freeSolo = false,
  onPick,
}) => {
  // Convert value to string for comparison since options are always strings
  const valueStr = value != null ? String(value) : null;
  // One lookup per render instead of a scan per option; lists can run to hundreds.
  const byValue = useMemo(() => new Map(options.map((opt) => [opt.value, opt])), [options]);
  const labelOf = (option: string) => {
    const label = byValue.get(option)?.label;
    if (label == null || label === "") return option;
    return typeof label === "string" ? label : String(label);
  };
  const selectedOption = valueStr != null ? byValue.get(valueStr) : undefined;
  // Free text is its own value; otherwise only a known option is shown.
  const displayValue = selectedOption ? selectedOption.value : freeSolo ? valueStr || null : null;

  return (
    <StyledAutocomplete
      value={displayValue}
      onChange={(event, newValue) => {
        if (onChange) {
          onChange(newValue);
        }
        if (onPick && typeof newValue === "string" && newValue.trim()) onPick(newValue);
      }}
      freeSolo={freeSolo}
      onInputChange={
        freeSolo
          ? (_event, text, reason) => {
              if (reason === "input") onChange?.(text);
            }
          : undefined
      }
      disabled={disabled || loading}
      options={options.map(opt => opt.value)}
      getOptionLabel={(option) => (option ? labelOf(option) : "")}
      getOptionDisabled={(option) => !!byValue.get(option)?.disabled}
      isOptionEqualToValue={(option, val) => option === val}
      fullWidth={fullWidth}
      size="small"
      popupIcon={<SelectDown />}
      // A free-text picker still opens a list, so it looks like the dropdown it is.
      forcePopupIcon
      filterOptions={(optionValues, { inputValue }) => {
        const searchLower = inputValue.trim().toLowerCase();
        if (!searchLower) return optionValues;
        return optionValues.filter(optionValue => {
          const opt = byValue.get(optionValue);
          if (opt?.pinned) return true;
          return (
            labelOf(optionValue).toLowerCase().includes(searchLower) ||
            optionValue.toLowerCase().includes(searchLower) ||
            (opt?.secondary || "").toLowerCase().includes(searchLower)
          );
        });
      }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          label={label}
          placeholder={!displayValue ? placeholder : undefined}
          InputProps={{
            ...params.InputProps,
            readOnly: false,
          }}
          inputProps={{
            ...params.inputProps,
            style: {
              ...params.inputProps.style,
              color: displayValue ? COLORS.foregroundTertiary : COLORS.foregroundSecondary,
            },
          }}
        />
      )}
      renderOption={(props: any, option) => {
        const opt = byValue.get(option);
        const isDisabled = opt?.disabled || false;
        const optionLabel = opt?.label || option;
        const secondary = opt?.secondary && opt.secondary !== optionLabel ? opt.secondary : null;
        return (
          <li
            {...props}
            key={option}
            style={{
              ...props.style,
              opacity: isDisabled ? 0.5 : 1,
              cursor: isDisabled ? "not-allowed" : "pointer",
            }}
          >
            {secondary ? (
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span>{optionLabel}</span>
                <span
                  style={{
                    fontSize: "0.75rem",
                    lineHeight: "1rem",
                    color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {secondary}
                </span>
              </span>
            ) : (
              optionLabel
            )}
          </li>
        );
      }}
      ListboxProps={{
        style: {
          maxHeight: 300,
        },
      }}
      PaperComponent={({ children, ...other }) => (
        <div
          {...other}
          style={{
            ...other.style,
            borderRadius: RADII.md,
            marginTop: 4,
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1)",
            backgroundColor: COLORS.backgroundWhite,
            border: `1px solid var(--color-borderTertiary, ${COLORS.borderTertiary})`,
          }}
        >
          {children}
        </div>
      )}
      slotProps={{
        popper: {
          placement: "bottom-start",
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 4],
              },
            },
          ],
        },
      }}
    />
  );
};

export default SearchableSelect;
